import {
  FindFoldersSortOption,
  FindItemsSortOption,
} from '@/models/item.models';
import { LocalStorage } from 'quasar';
import { ComputedRef, Ref, unref, watch } from 'vue';
import { decodeBase64, encodeBase64 } from './encode-decode';
import { isObject, omit } from 'lodash-es';
import { useOrganizationStore } from '@/store/organizationStore';

export enum FeatureCodes {
  EARLY_ACCESS = 'early-access',
}

export enum LocalStorageKeys {
  ACCESS_TOKEN = 'ds:accessToken',
  REFRESH_TOKEN = 'ds:refreshToken',
  ID_TOKEN = 'ds:idToken',
  USER = 'ds:user',
  CACHED_USERS = 'ds:allUsers',
  CACHED_TEAMS = 'ds:allTeams',
  CURRENT_ORGANIZATION = 'ds:current_organization',
  LAST_VIEWED_ORGANIZATION_ID = 'ds:last_viewed_organization_id',
  REDIRECT_URL = 'ds:redirectUrl',
  SIDE_NAV_EXPANDED = 'ds:sideNavExpanded',
  LAST_SEEN_RELEASE_NAME = 'ds:lastSeenReleaseName', // @deprecated - use LAST_SEEN_RELEASE_URL
  LAST_SEEN_RELEASE_URL = 'ds:lastSeenReleaseUrl',
  LAST_SEEN_RELEASE_DATE = 'ds:lastSeenReleaseDate',
  PLAN_ITEMS_APPLIED_FILTERS = 'ds:planItemsAppliedFilters',
  PLAN_BOARDS_APPLIED_FILTERS = 'ds:planBoardsAppliedFilters',
  FILTER_PROMPTS = 'ds:filterPrompts',
  PLAN_BOARD_SORT = 'ds:planBoardSort',
  PLAN_ITEM_SORT = 'ds:planItemsSort',
  KANBAN_LANE_COLLAPSED = 'ds:kanbanLaneCollapsed',
  PORTFOLIO_APPLIED_FILTERS = 'ds:portfolioAppliedFilters',
  NOTIFICATIONS_APPLIED_FILTERS = 'ds:notificationsAppliedFilters',
  AUTOMATIONS_APPLIED_FILTERS = 'ds:automationsAppliedFilters',
  AUTOMATIONS_COLLAPSED_GROUPS = 'ds:automationsCollapsedGroups',
  MY_ITEMS_APPLIED_FILTERS = 'ds:myItemsAppliedFilters',
  MY_ITEMS_SELECTED_OPTION = 'ds:myItemsSelectedOption',
  SELECTED_PANEL_FOLDER_NUMBER = 'ds:selectedPanelFolderNumber',
  MAP_WORKSTREAM_SORT = 'ds:mapWorkstreamSort',
  MAP_ITEM_SORT = 'ds:mapItemSort',
  TOOLBAR_COLLAPSED = 'ds:toolbarCollapsed',
  HIDE_TRIAL_ALERT_UNTIL = 'ds:hideTrialAlertUntil',
  HIDE_INVALID_PAYMENT_ALERT_UNTIL = 'ds:hideInvalidPaymentAlertUntil',
  RECENT_ITEMS = 'ds:recentItems',
  WORKSPACE_ASSETS_SHOW_PARENTS = 'ds:workspaceAssetsShowParents',
  WORKSTREAMS_LINE_WRAP = 'ds:workstreamsLineWrap',
  WORKSTREAMS_SHOW_COMPLETION_STATS_ICON = 'ds:workstreamsShowCompletionStatsIcon',
  WORKSTREAMS_SHOW_ITEM_NUMBER = 'ds:workstreamsShowItemNumber',
  WORKSTREAMS_ITEM_INDICATOR = 'ds:workstreamsItemIndicator',
  ANALYZE_SELECTED_WORK_TYPE_IDS = 'ds:analyzeSelectedWorkTypeIds',
  // Unarchive Items Dialog Do Not Show
  UNARCHIVE_ITEMS_MESSAGE_DO_NOT_SHOW = 'ds:unarchiveItemsMessageDoNotShow',
  // Unarchive Items Child Items as well
  UNARCHIVE_ITEMS_CHILDREN_AS_WELL = 'ds:unarchiveItemsChildrenAsWell',
  /** @template - `Record<PreparedFolder['number'], number>` */
  PORTFOLIO_COLUMN_STORED_SIZES = 'ds:portfolioColumnStoredSizes',
  /** @template - `Record<Lane['id'], number>` */
  KANBAN_LANES_STORED_SIZES = 'ds:kanbanLanesStoredSizes',

  // Analyze Applies filters by report
  ANALYZE_FILTERS = 'ds:analyzeFilters',
  ANALYZE_SELECTED_VIEW_OPTION = 'ds:analyzeSelectedViewOption',
  ANALYZE_CHURN_ANALYZE_BY = 'ds:analyzeChurnAnalyzeBy',
  ANALYZE_USER_TIME_TRACKING_ASSIGNEE = 'ds:analyzeUserTimeTrackingAssignee',
  ANALYZE_USER_TIME_TRACKING_GROUP_BY = 'ds:analyzeUserTimeTrackingGroupBy',

  // Chart options
  CHART_HIDE_X_AXIS_VALUES = 'ds:chartHideXAxisValues',
  CHART_HIDE_X_AXIS_LABELS = 'ds:chartHideXAxisLabels',
  CHART_HIDE_Y_AXIS_VALUES = 'ds:chartHideYAxisValues',
  CHART_HIDE_Y_AXIS_LABELS = 'ds:chartHideYAxisLabels',
  CHART_HIDE_SERIES_VALUES = 'ds:chartHideSeriesValues',
  CHART_HIDE_SERIES_LABELS = 'ds:chartHideSeriesLabels',
  CHART_LAST_OVERVIEW_INSIGHTS_SUMMARIZE_BY_LABEL = 'ds:chartLastOverviewInsightsSummarizeByLabel',
  CHART_LAST_TRENDING_PROGRESS_ANALYZE_BY = 'ds:chartLastTrendingProgressAnalyzeBy',

  // Board options
  SELECTED_TWO_D_BOARD_TICKS = 'ds:selectedTwoDBoardTicks',
  KANBAN_BOARD_HIDDEN_OPTIONS = 'ds:kanbanBoardHiddenOptions_1',
  KANBAN_UI_VIEW = 'ds:boardUiView',

  ITEMS_TABLE_COLUMNS = 'ds:itemsTableColumnData',
  SUB_ITEMS_TABLE_COLUMNS = 'ds:subItemsTableColumnDat_1', // _1 for cache busting;
  ITEMS_TABLE_COLUMNS_IN_PLAN_BOARDS = 'ds:itemsTableColumnDataInPlanBoards',

  SHOW_SUBTASKS_IN_BOARD_VIEW_TABLE = 'ds:showSubtasksInBoardViewTable',
  SHOW_SUBTASKS_IN_ITEMS_VIEW_TABLE = 'ds:showSubtasksInItemsViewTable',
  SHOW_SUBTASKS_IN_MY_WORK_TABLE = 'ds:showSubtasksInMyWorkTable',

  LINE_WRAP_IN_BOARD_VIEW_TABLE = 'ds:lineWrapInBoardViewTable',
  LINE_WRAP_IN_ITEMS_VIEW_TABLE = 'ds:lineWrapInItemsViewTable',
  LINE_WRAP_IN_MY_WORK_TABLE = 'ds:lineWrapInMyWorkTable',

  QUICK_ADD_LAST_SELECTED_WORKSTREAM_NUMBER = 'ds:quickAddLastSelectedWorkstreamNumber',
  QUICK_ADD_LAST_SELECTED_PARENT_ITEM_NUMBER = 'ds:quickAddLastSelectedParentItemNumber',
  QUICK_ADD_LAST_CUSTOM_FIELD_VALUE_PREFIX = 'ds:quickAddLastCustomFieldValue_',
  QUICK_ADD_OPEN_IN_DRAWER_AFTER_CREATION = 'ds:quickAddOpenInDrawerAfterCreation',
  DEFAULT_MAP_RANK = 'ds:defaultMapRank',

  // Gantt/roadmap stuff
  ROADMAP_SELECTED_ID = 'ds:roadmapSelectedId',
  ROADMAP_APPLIED_FILTERS = 'ds:roadmapAppliedFilters',
  /** @template - `InteractionMode` */
  ROADMAP_SELECTED_INTERACTION_MODE = 'ds:roadmapSelectedInteractionMode',
  /** @template - `{ [roadmapId: GanttRoadmap['id']]: PreparedFolder['number'][] }` */
  ROADMAP_EXPANDED_FOLDERS = 'ds:roadmapExpandedFolders',
  /** @template - `{ [roadmapId: GanttRoadmap['id']]: ZOOM_LEVEL_TYPE }` */
  ROADMAP_ZOOM_LEVEL = 'ds:roadmapZoomLevel',
  /** @template - `{ [roadmapId: GanttRoadmap['id']]: number }` - fine-grained zoom index */
  ROADMAP_ZOOM_LEVEL_IDX = 'ds:roadmapZoomLevelIdx',
  /** @template - `{ [roadmapId: GanttRoadmap['id']]: number }` - row height in pixels */
  ROADMAP_ROW_HEIGHT = 'ds:roadmapRowHeight',
  /** @template - `{ [roadmapId: GanttRoadmap['id']]: { horizontal: string | null; vertical: number | null } }` - scroll center point (date ISO string for horizontal, row index for vertical) */
  ROADMAP_SCROLL_CENTER_POINT = 'ds:roadmapScrollCenterPoint',
  /** used by the gantt chart's `persistence-key` prop */
  GANTT_INTERNAL_STATE = 'ds:ganttInternalState',
  GANTT_BAD_DATA_DO_NOT_SHOW = 'ds:ganttBadDataDoNotShow',

  // Jobs
  JOBS_TABLE_COLUMNS = 'ds:jobsTableColumnData',

  // Cadence
  CADENCE_GANTT_INTERNAL_STATE = 'ds:cadenceGanttInternalState',

  /** @template - `{ [organizationId: Organization['id'], urlSuffix: string }` */
  LAST_VISITED_BOARD_VIEW = 'ds:lastVisitedBoardView',

  SHOW_ARCHIVED_FOLDERS = 'ds:showArchivedFolders',
  BOARD_QUICK_ACCESS_OPEN = 'ds:boardQuickAccessOpen',
  BOARD_FOLDER_COLUMN_WIDTH = 'ds:boardFolderColumnWidth',

  // Stored view
  STORED_VIEWS = 'ds:storedViews',
  STORED_VIEWS_AFTER_PORT_BACKUP = 'ds:storedViewsAfterPortBackup',

  /**
   * Last selected child work types for specific parent work types
   * @template - `Record<WorkType['id'] | 'folder', WorkType['id']>`
   **/
  LAST_SELECTED_WORK_TYPES = 'ds:lastSelectedWorkTypes',

  /** master search */
  MASTER_SEARCH_RECENTS = 'ds:masterSearchRecents',
  MASTER_SEARCH_VIEW_TYPE = 'ds:masterSearchViewType',

  /** board selector view mode preference (browse/recents) */
  BOARD_SELECTOR_VIEW_MODE = 'ds:boardSelectorViewMode',

  /** item selector view mode preference (browse/recents) */
  ITEM_SELECTOR_VIEW_MODE = 'ds:itemSelectorViewMode',

  COMMENT_TIME_FORMAT = 'ds:commentTimeFormat',

  TWO_D_BOARD_INTERNAL_STATE = 'ds:twoDBoardInternalState',

  /** all things related to jira and ado integrations */
  JIRA_JOBS_TABLE_COLUMNS = 'ds:jiraJobsTableColumnData',
  ADO_JOBS_TABLE_COLUMNS = 'ds:adoJobsTableColumnData',

  /** draft comments storage */
  DRAFT_COMMENTS = 'ds:draftComments',

  /** Time tracking table */
  TIME_TRACKING_TABLE_COLUMNS = 'ds:timeTrackingTableColumnData',

  /** AI Bulk Item Creation preferences */
  AI_BULK_USE_CUSTOM_INSTRUCTIONS = 'ds:aiBulkUseCustomInstructions',
  AI_BULK_SELECTED_INSTRUCTION_TEMPLATE = 'ds:aiBulkSelectedInstructionTemplate',
  AI_BULK_USE_STRUCTURED_FORMAT = 'ds:aiBulkUseStructuredFormat',
  AI_BULK_SELECTED_FORMAT_TEMPLATE = 'ds:aiBulkSelectedFormatTemplate',

  /** Time duration input mode preference ('natural' | 'ledger') */
  TIME_DURATION_INPUT_MODE = 'ds:timeDurationInputMode',
}

export function orgScopedKey(key: LocalStorageKeys) {
  const organizationStore = useOrganizationStore();
  const organizationId = organizationStore.activeOrganization?.id;
  if (organizationId) {
    return `${key}_${organizationId}`;
  }
  return key;
}

export function useLastViewedBoardView() {
  const organizationStore = useOrganizationStore();

  const setLastVisitedBoardView = (urlSuffix: string) => {
    const organizationId = organizationStore.activeOrganization?.id;
    if (organizationId) {
      const key = `${LocalStorageKeys.LAST_VISITED_BOARD_VIEW}_${organizationId}`;
      LocalStorage.set(key, urlSuffix);
    }
  };

  const getLastVisitedBoardView = () => {
    const organizationId = organizationStore.activeOrganization?.id;
    if (organizationId) {
      const key = `${LocalStorageKeys.LAST_VISITED_BOARD_VIEW}_${organizationId}`;
      return LocalStorage.getItem(key) || '';
    }
    return '';
  };

  return { setLastVisitedBoardView, getLastVisitedBoardView };
}

export function clearOrgDataFromLocalStorage() {
  LocalStorage.remove(LocalStorageKeys.CURRENT_ORGANIZATION);
}

export function setLocalStorageKey(
  key: LocalStorageKeys | string,
  value: string | number | boolean | object | null
) {
  LocalStorage.set(key, value);
}

export function getLocalStorageKey<
  T = string | number | boolean | object | null
>(key: LocalStorageKeys | string): T | null {
  return LocalStorage.getItem(key) as T | null;
}

export function removeLocalStorageKey(key: LocalStorageKeys | string) {
  LocalStorage.remove(key);
}

export function useFilterLocalStorage<T>(
  key: ComputedRef<string> | string,
  exclude: string[] = []
) {
  let existingFilters: T;
  watch(
    () => unref(key),
    () => {
      existingFilters = (LocalStorage.getItem(unref(key)) || {}) as T;
      if (!isObject(existingFilters)) {
        existingFilters = {} as T;
      }
    },
    { immediate: true }
  );

  function setLocalStorageFilters(filters: T) {
    const incomingFilters = omit(filters as Record<string, unknown>, exclude);

    // Start with existing filters
    const newFilters = { ...existingFilters } as Record<string, unknown>;

    // Apply incoming filters, removing keys that are explicitly undefined
    for (const [filterKey, value] of Object.entries(incomingFilters)) {
      if (value === undefined) {
        delete newFilters[filterKey];
      } else {
        newFilters[filterKey] = value;
      }
    }

    existingFilters = newFilters as T;
    LocalStorage.set(unref(key), newFilters);
  }

  function getLocalStorageFilters() {
    const keyUnref = unref(key);
    let filters = LocalStorage.getItem(keyUnref);
    if (!isObject(filters)) {
      filters = {};
      LocalStorage.remove(keyUnref);
    }
    const ret = {
      ...omit(filters as Record<string, unknown>, exclude),
    } as Record<string, unknown>;
    return ret as T;
  }
  return { getLocalStorageFilters, setLocalStorageFilters };
}

export function useSortLocalStorage({
  sortOptions,
  defaultSort,
  localStorageKey,
}: {
  sortOptions: Ref<{
    direction: 'asc' | 'desc';
    sort: FindItemsSortOption | FindFoldersSortOption;
  }>;
  defaultSort:
    | ComputedRef<{
        direction: 'asc' | 'desc';
        sort: FindItemsSortOption | FindFoldersSortOption;
      }>
    | {
        direction: 'asc' | 'desc';
        sort: FindItemsSortOption | FindFoldersSortOption;
      };
  localStorageKey: ComputedRef<string> | string;
}) {
  function saveSortToLocalStorage() {
    const base64Sort = encodeBase64(sortOptions.value);
    const key = unref(localStorageKey);
    LocalStorage.set(key, base64Sort);
  }

  function setSortFromLocalStorage() {
    const key = unref(localStorageKey);
    const base64Sort = LocalStorage.getItem(key);
    if (!base64Sort || typeof base64Sort !== 'string') {
      sortOptions.value = unref(defaultSort);
    } else {
      try {
        const sortObject = decodeBase64<{
          direction: 'asc' | 'desc';
          sort: FindItemsSortOption | FindFoldersSortOption;
        }>(base64Sort);
        sortOptions.value = sortObject;
      } catch (e) {
        console.error(e);
        sortOptions.value = unref(defaultSort);
      }
    }
  }

  return {
    saveSortToLocalStorage,
    setSortFromLocalStorage,
  };
}
